/**
 *
 * Javascript behavior for: schools shortcode
 *
 * The first page of every education level is rendered server side; further
 * pages are fetched from the schools REST endpoint on demand.
 */
Mmg.addShortcode(

  /**
   * @param {string} shortcodeName The name of the shortcode
   * @param {string} selector The selector used to locate this shortcode in the DOM
   * @param {callable} callback The callback function applied to every element matching the selector
   */
  'schools',
  '.sc-schools',

  /**
   *
   * Shortcode callback
   *
   * The shortcode callback receives the following parameters:
   *
   * @param {Element} el The shortcode's DOM element
   * @param {object} model The data model passed to the shortcode instance in data-json attribute
   * @param {jQuery} $ The callback function applied to every element matching the selector
   * @param {string} selector The shortcode selector that was passed to addShortcode
   * @param {string} className The name of the shortcode class
   */
  (el, model, $, selector, className) => {
  // Widget Declarations
  const $this = $(el);
  const templateRow = $this.find(`${selector}__template-row`).html();
  const pageSize = model.page_size;
  const levels = {};

  /**
   * Initialize the widget
   *
   * The model carries every education level, but only levels with more
   * schools than fit on one page have pagination controls in the DOM.
   */
  (function init() {
    (model.levels || []).forEach(initLevel);
  })();


  /**
   * Set up one education level's pager. Levels that fit on a single page
   * have no controls in the DOM to wire up, and are left alone. The
   * controls the server rendered are already correct for page one, so
   * there is nothing to update here.
   *
   * @param {object} level The level's name and total school count
   */
  function initLevel(level) {
    const $panel = $this.find(`${selector}__tab-pane--${level.name}`);
    const $body = $panel.find(`${selector}__table-body`);
    const $prev = $panel.find('.pagination__link--prev');
    const $next = $panel.find('.pagination__link--next');

    if (!$prev.length) {
      return;
    }

    levels[level.name] = {
      $body: $body,
      $prev: $prev,
      $next: $next,
      $range: $panel.find('.pagination__page-range'),
      total: level.total,
      lastPage: Math.ceil(level.total / pageSize),
      page: 1,
      loading: false,
    };

    $prev.on('click', (event) => onPrevClick(level.name, event));
    $next.on('click', (event) => onNextClick(level.name, event));
  }


  /**
   * @param {string} levelName
   * @param {Event} event
   */
  function onPrevClick(levelName, event) {
    event.preventDefault();
    goToPage(levelName, levels[levelName].page - 1);
  }


  /**
   * @param {string} levelName
   * @param {Event} event
   */
  function onNextClick(levelName, event) {
    event.preventDefault();
    goToPage(levelName, levels[levelName].page + 1);
  }


  /**
   * Move a level to a page, always fetching it fresh. The current page
   * stays in place while the request is in flight.
   *
   * @param {string} levelName
   * @param {number} target The page to move to, 1 based
   */
  function goToPage(levelName, target) {
    const level = levels[levelName];

    if (level.loading || target < 1 || target > level.lastPage) {
      return;
    }

    level.loading = true;

    $.getJSON(model.api_url, {
      entity_id: model.entity_id,
      source: model.source,
      education_level: levelName,
      page: target,
      page_size: pageSize,
    })
      .done((data) => {
        level.$body.html(data.schools.map(renderRow).join(''));
        level.page = target;
      })
      .always(() => {
        level.loading = false;
        updatePagination(levelName);
      });
  }


  /**
   * Render one school's table row using the embedded Mustache template.
   *
   * @param {object} school
   * @return {string}
   */
  function renderRow(school) {
    return Mustache.render(
      templateRow,
      $.extend({ _block: className, is_district: model.source === 'district' }, school)
    );
  }


  /**
   * Bring a level's pagination controls in sync with its current page.
   *
   * @param {string} levelName
   */
  function updatePagination(levelName) {
    const level = levels[levelName];
    const start = ((level.page - 1) * pageSize) + 1;
    const end = Math.min(level.page * pageSize, level.total);

    level.$range.text(`${start}–${end} of ${level.total}`);
    setLinkDisabled(level.$prev, level.page === 1);
    setLinkDisabled(level.$next, level.page === level.lastPage);

    if (model.source === 'district') {
      setLinkTracking(level.$prev, level.page - 1, level.page === 1);
      setLinkTracking(level.$next, level.page + 1, level.page === level.lastPage);
    }
  }


  /**
   * Disabled links stay clickable, so they drop their label instead of
   * reporting a page that is never landed on.
   *
   * @param {jQuery} $link
   * @param {number} page The page the link lands on
   * @param {boolean} disabled
   */
  function setLinkTracking($link, page, disabled) {
    if (disabled) {
      $link.removeAttr('data-tracking');
    } else {
      $link.attr('data-tracking', `local:schools:next_${page}`);
    }
  }


  /**
   * @param {jQuery} $link
   * @param {boolean} disabled
   */
  function setLinkDisabled($link, disabled) {
    $link.toggleClass('disabled', disabled);
    if (disabled) {
      $link.attr({ 'aria-disabled': 'true', tabindex: '-1' });
    } else {
      $link.removeAttr('aria-disabled').removeAttr('tabindex');
    }
  }

});
